import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container, Box, Typography, Paper, Button, Chip, Alert, CircularProgress, Divider,
  IconButton, Tooltip, Snackbar, Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { carePlanService, carePlanErrorMessage } from "../../services/carePlanService";
import CarePlanAdherenceBanner from "../../components/CarePlanAdherenceBanner";
import CarePlanTaskDialog from "../../components/care-plan/CarePlanTaskDialog";
import AddActivityDialog from "../../components/care-plan/AddActivityDialog";
import { MEASUREMENTS, WEEKDAYS } from "../../components/care-plan/CarePlanEditorDialog";

const GOAL_LABEL = { OPEN: "In progress", MET: "Reached", DROPPED: "Set aside" };
const GOAL_COLOR = { OPEN: "default", MET: "success", DROPPED: "default" };

const measurementLabel = (type) => MEASUREMENTS.find((m) => m.type === type)?.label || type;
const weekdayLabel = (n) => WEEKDAYS.find((d) => d.value === n)?.label || "";

// Date-only values are built as local calendar dates so the browser's
// timezone can never shift them by a day.
const fmtDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
};
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString() : "");

/**
 * The patient's own view of their care plan: what they're asked to do, their
 * goals, and what to log. A patient can mark THEIR tasks done (or undo that), add a
 * note, and log extra activities of their own ("Ran an extra mile"). They can remove
 * an extra they added, but can't change the plan, cancel a task the care team gave
 * them, or see staff tasks. The server enforces all of that, so this page only shows
 * what it receives.
 */
export default function MyCarePlanPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(undefined); // undefined = loading, null = no active plan
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [noteTask, setNoteTask] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD, local

  const load = useCallback(async () => {
    try {
      const plans = await carePlanService.list(patientId);
      setPlan(plans.find((p) => p.status === "ACTIVE") || null);
    } catch (err) {
      setError(carePlanErrorMessage(err, "We couldn't load your care plan. Please try again."));
      setPlan(null);
    }
  }, [patientId]);

  useEffect(() => {
    load();
    // Anything that changes the plan announces it (including the banner's own buttons),
    // so this list never goes stale.
    window.addEventListener("careplan:changed", load);
    return () => window.removeEventListener("careplan:changed", load);
  }, [load]);

  const announce = () => window.dispatchEvent(new Event("careplan:changed"));

  const updateTask = async (task, body, message) => {
    setBusy(true);
    setError(null);
    try {
      await carePlanService.updateTask(patientId, plan.id, task.id, body);
      setNotice(message);
      announce(); // reloads this page and the banner
      return true;
    } catch (err) {
      setError(carePlanErrorMessage(err, "That didn't save. Please try again."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addActivity = async (payload) => {
    setBusy(true);
    setError(null);
    try {
      await carePlanService.addTask(patientId, plan.id, payload);
      setAddOpen(false);
      setNotice(payload.alreadyDone ? "Logged — nice!" : "Added to your to-do list");
      announce();
    } catch (err) {
      setError(carePlanErrorMessage(err, "That didn't save. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const removeActivity = (task) => {
    if (window.confirm("Remove this activity?")) updateTask(task, { status: "CANCELLED" }, "Removed");
  };

  if (plan === undefined) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const tasks = plan ? plan.tasks.filter((t) => t.status !== "CANCELLED") : [];
  const openTasks = tasks
    .filter((t) => t.status === "OPEN")
    .sort((a, b) => (a.dueDate || "9999-12-31").localeCompare(b.dueDate || "9999-12-31"));
  const doneTasks = tasks.filter((t) => t.status === "DONE");

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>My Care Plan</Typography>
          {plan && (
            <Typography color="text.secondary">
              {plan.title}
              {plan.nextReviewDate ? ` · Next review ${fmtDate(plan.nextReviewDate)}` : ""}
            </Typography>
          )}
        </Box>
        <Button variant="contained" startIcon={<EventNoteIcon />} onClick={() => navigate(`/patients/${patientId}/daily-intake`)}>
          Today's check-in
        </Button>
      </Box>

      {error && !noteTask && !addOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!plan ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>No care plan yet</Typography>
          <Typography color="text.secondary">
            Your care team hasn't set up a plan for you yet. When they do, your goals, tasks and
            what to log will show up here.
          </Typography>
        </Paper>
      ) : (
        <>
          <CarePlanAdherenceBanner patientId={patientId} audience="patient" showLink={false} showTasks={false} />

          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>Your tasks</Typography>
              <Button size="small" startIcon={<AddIcon />} disabled={busy} onClick={() => { setError(null); setAddOpen(true); }}>
                Add an activity
              </Button>
            </Box>
            {openTasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {doneTasks.length > 0 ? "You're all caught up. 🎉" : "Nothing to do right now."}
              </Typography>
            ) : (
              openTasks.map((t, idx) => {
                const overdue = t.dueDate && t.dueDate < todayStr;
                return (
                  <Box key={t.id}>
                    {idx > 0 && <Divider />}
                    <Box sx={{ display: "flex", gap: 1.5, py: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography fontWeight={600} component="span">{t.title}</Typography>
                        {t.patientAdded && <Chip size="small" variant="outlined" label="Added by me" sx={{ ml: 1 }} />}
                        {t.dueDate && (
                          <Box>
                            <Chip size="small" variant={overdue ? "filled" : "outlined"} color={overdue ? "error" : "default"}
                              label={`${overdue ? "Overdue · " : "Due "}${fmtDate(t.dueDate)}`} sx={{ my: 0.5 }} />
                          </Box>
                        )}
                        {t.description && <Typography variant="body2" color="text.secondary">{t.description}</Typography>}
                        {t.notes && (
                          <Typography variant="body2" sx={{ fontStyle: "italic", mt: 0.5 }}>My note: {t.notes}</Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                        <Button size="small" variant="contained" color="success" startIcon={<CheckCircleOutlineIcon />}
                          disabled={busy} onClick={() => updateTask(t, { status: "DONE" }, "Nice work — marked done")}>
                          Mark done
                        </Button>
                        <Tooltip title={t.notes ? "Edit my note" : "Add a note"}>
                          <span>
                            <IconButton size="small" disabled={busy} onClick={() => { setError(null); setNoteTask(t); }}>
                              <NoteAddOutlinedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {t.patientAdded && (
                          <Tooltip title="Remove this activity">
                            <span>
                              <IconButton size="small" disabled={busy} onClick={() => removeActivity(t)}>
                                <CancelOutlinedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}

            {doneTasks.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Completed</Typography>
                {doneTasks.map((t) => (
                  <Box key={t.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, py: 0.5 }}>
                    <Box>
                      <Typography variant="body2" component="span" sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                        {t.title}
                      </Typography>
                      {t.patientAdded && <Chip size="small" variant="outlined" label="Added by me" sx={{ ml: 1 }} />}
                      {t.completedAt && (
                        <Typography variant="caption" color="text.secondary" display="block">Done {fmtDateTime(t.completedAt)}</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Tooltip title="Undo — mark as not done">
                        <span>
                          <IconButton size="small" disabled={busy} onClick={() => updateTask(t, { status: "OPEN" }, "Moved back to your to-do list")}>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {t.patientAdded && (
                        <Tooltip title="Remove this activity">
                          <span>
                            <IconButton size="small" disabled={busy} onClick={() => removeActivity(t)}>
                              <CancelOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Paper>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>My goals</Typography>
                {plan.goals.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No goals set yet.</Typography>
                ) : (
                  plan.goals.map((g) => (
                    <Box key={g.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.75 }}>
                      <Box>
                        <Typography variant="body2">{g.description}</Typography>
                        {g.targetDate && (
                          <Typography variant="caption" color="text.secondary">Aim for {fmtDate(g.targetDate)}</Typography>
                        )}
                      </Box>
                      <Chip size="small" color={GOAL_COLOR[g.status]} variant={g.status === "OPEN" ? "outlined" : "filled"}
                        label={GOAL_LABEL[g.status] || g.status} />
                    </Box>
                  ))
                )}
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>What to log</Typography>
                {plan.monitoring.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No regular readings are needed.</Typography>
                ) : (
                  plan.monitoring.map((m) => (
                    <Box key={m.id} sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
                      <Typography variant="body2">{measurementLabel(m.measurementType)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {m.frequency === "WEEKLY" ? `Every ${weekdayLabel(m.dayOfWeek)}` : "Every day"}
                      </Typography>
                    </Box>
                  ))
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Always tell us about new symptoms in your daily check-in, even if they aren't part of your plan.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {plan.notes && (
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Notes from your care team</Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{plan.notes}</Typography>
            </Paper>
          )}
        </>
      )}

      <CarePlanTaskDialog open={!!noteTask} mode="note" task={noteTask} staff={[]} saving={busy} error={error}
        onClose={() => setNoteTask(null)}
        onSave={async ({ notes }) => {
          if (await updateTask(noteTask, { notes }, "Note saved")) setNoteTask(null);
        }} />
      <AddActivityDialog open={addOpen} saving={busy} error={error} onClose={() => setAddOpen(false)} onSave={addActivity} />

      <Snackbar open={!!notice} autoHideDuration={3500} onClose={() => setNotice(null)}>
        <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>
      </Snackbar>
    </Container>
  );
}